import FormTextarea from '@c/forms/controls/FormTextarea';
import { CheckIcon, StarIcon } from '@c/icons';
import { ProductDocument } from '@models/product';
import { ReviewDocument } from '@models/review';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Avatar from '@ui/Avatar';
import Button from '@ui/Button';
import Checkbox from '@ui/Checkbox';
import Spinner from '@ui/Spinner';
import { cleanProductReview, submitReview } from '@util/firestore/reviews';
import { getPublicUserDoc } from '@util/firestore/users';
import { findVariation, isMobile } from '@util/index';
import { useAuth } from 'context/AuthContext';
import React from 'react';
import BaseModal from './BaseModal';
import useMultiImageUpload from '@util/hooks/useMultiImageUpload';
import { cx } from 'class-variance-authority';
import LoadingSpinner from '@c/LoadingSpinner';
import { AddPhotoIcon } from '@c/listing/icons/AddPhotoIcon';
import { ImageList } from '@c/listing/components/ImageList';
import { OrderDocument } from '@models/order';

interface ButtonChoicesProps {
  choices: {
    label: string;
    icon?: React.ReactNode;
    onClick?: () => void;
    selected: boolean;
  }[];
}

export const ButtonChoices = ({ choices }: ButtonChoicesProps) => {
  const [selectedBtn, setSelectedBtn] = React.useState<string | null>(
    choices.find((choice) => choice.selected)?.label || null
  );
  return (
    <div className="flex w-full items-start gap-[0.8rem]">
      {choices.map((choice) => (
        <button
          className={`flex grow flex-col  items-center gap-[0.4rem] rounded-xl border-2 bg-brand-secondary-light px-[1.2rem] py-[1rem] transition-colors ${
            selectedBtn === choice.label
              ? 'border-brand-primary bg-brand-primary-lighter text-brand-primary'
              : ''
          }`}
          key={choice.label}
          onClick={() => {
            setSelectedBtn(choice.label);
            choice.onClick?.();
          }}
        >
          {choice.icon}
          {choice.label}
        </button>
      ))}
    </div>
  );
};

interface LeaveFeedbackModalProps {
  isOpen: boolean;
  dismiss?: () => void;
  order: OrderDocument;
  reviewer_id: string;
  seller_id: string | null;
  products: ProductDocument[];
}

const LeaveFeedbackModal = ({
  isOpen,
  dismiss,
  order,
  reviewer_id,
  seller_id,
  products,
}: LeaveFeedbackModalProps) => {
  const { userDoc } = useAuth();
  const [loadingSubmit, setLoadingSubmit] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [review, setReview] = React.useState<
    Omit<ReviewDocument, 'id' | 'created' | 'username_slug'>
  >({
    rating: 5,
    quick_replies: false,
    fast_shipper: false,
    as_described: false,
    from_web: true,
    comment: '',
    is_anon: false,
    uid: seller_id ?? '',
    order_id: order.id,
    reviewer_photo: userDoc?.photo ?? '',
    reviewer_username: userDoc?.username ?? '',
    reviewer_id,
    products: products.map((product) => {
      const seller = order.sellers[seller_id ?? ''];
      const item = seller?.find((i) => i.id === product.id);
      const thumbnail =
        item?.variations && product.variations?.length
          ? findVariation(product, item.variations)?.image ?? product.thumbnail
          : product.thumbnail;
      return {
        ...cleanProductReview(product),
        thumbnail,
      };
    }),
    images: [],
    replies: [],
  });
  const queryClient = useQueryClient();

  const { data: sellerDoc, isLoading } = useQuery({
    queryKey: ['publicUser', seller_id],
    queryFn: () => getPublicUserDoc({ uid: seller_id!, noCache: true }),
    enabled: !!seller_id,
  });

  const { uploadingImage, handleFileInputChange, deleteImage } =
    useMultiImageUpload({
      id: `${order.id}_${seller_id}`,
      type: 'review',
      onUploadCallback: ({ paths }) => {
        setReview({ ...review, images: [...(review.images ?? []), ...paths] });
      },
      onDeleteCallback: (paths) => {
        setReview({ ...review, images: paths });
      },
    });

  const submitReviewMutation = useMutation({
    onMutate: () => setLoadingSubmit(true),
    mutationFn: async (review: Partial<ReviewDocument>) => {
      await submitReview(review);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getReviewByOrderId'],
      });
      queryClient.invalidateQueries({
        queryKey: ['order'],
      });
      queryClient.invalidateQueries({
        queryKey: ['orders'],
      });
      queryClient.invalidateQueries({
        queryKey: ['purchases'],
      });
      queryClient.invalidateQueries({
        queryKey: ['pendingReviewsCount'],
      });
      queryClient.invalidateQueries({
        queryKey: ['review'],
      });
      dismiss?.();
    },
    onSettled: () => setLoadingSubmit(false),
  });

  if (isLoading)
    return (
      <div className="mx-auto h-[10rem] w-[10rem]">
        <Spinner />
      </div>
    );
  if (!sellerDoc) return null;

  const getRatingText = () => {
    switch (review.rating) {
      case 1:
        return "Terrible seller :'(";
      case 2:
        return 'Very bad seller :(';
      case 3:
        return 'Average seller...';
      case 4:
        return 'Very good seller!';
      case 5:
        return 'Excellent seller!';
      default:
        return 'Average seller';
    }
  };
  return (
    <BaseModal
      isOpen={isOpen}
      dismiss={() => dismiss?.()}
      title="Leave Feedback"
      isFullScreen={isMobile()}
    >
      <div className="flex max-h-screen flex-col items-start gap-[2rem] overflow-y-auto p-[2.4rem] lg:w-[50rem]">
        {/* seller profile */}
        <div className="flex items-center gap-[1.6rem]">
          <Avatar user={sellerDoc} />
          <div className="flex flex-col items-start gap-[0.4rem]">
            <div className="flex items-center gap-[0.8rem]">
              <span className="text-[1.6rem] font-semibold leading-[2.4rem] text-brand-lightest-black">
                {sellerDoc.username}
              </span>
              <div className="rounded-full bg-brand-primary text-white">
                <CheckIcon />
              </div>
            </div>
            <div className="flex items-center gap-[0.4rem]">
              <div className="flex items-center gap-2">
                <StarIcon />
                <span className="text-brand-lightest-black">
                  {sellerDoc.rating.toFixed(2)}
                </span>
              </div>
              <span className="text-brand-gray">
                ({sellerDoc.reviews} reviews)
              </span>
            </div>
          </div>
        </div>
        {/* rate this seller */}
        <div className="flex w-full flex-col items-start justify-center gap-[0.8rem]">
          <span className="text-[1.5rem] leading-[2.4rem] text-brand-lightest-black">
            Rate this seller:
          </span>
          <div className="flex w-full flex-col items-start justify-between gap-[1.6rem] sm:flex-row sm:items-center">
            {/* stars */}
            <div className="flex items-center gap-[0.8rem]">
              {Array.from({ length: 5 }, (_, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setReview({ ...review, rating: i + 1 });
                  }}
                >
                  <StarIcon
                    width={35}
                    height={33}
                    fill={review.rating < i + 1 ? '#D9D9D9' : '#c41819'}
                  />
                </button>
              ))}
            </div>
            <span className="font-semibold text-brand-secondary">
              {getRatingText()}
            </span>
          </div>
        </div>
        {/* seller communication */}
        <div className="flex w-full flex-wrap justify-around gap-[0.8rem]">
          <Checkbox
            key={'Fast Shipper'}
            selected={review.fast_shipper}
            onChange={() =>
              setReview({ ...review, fast_shipper: !review.fast_shipper })
            }
            label="Fast Shipper"
          />
          <Checkbox
            key={'Item as Described'}
            selected={review.as_described}
            onChange={() =>
              setReview({ ...review, as_described: !review.as_described })
            }
            label="Item as Described"
          />
          <Checkbox
            key={'Quick Replies'}
            selected={review.quick_replies}
            onChange={() =>
              setReview({ ...review, quick_replies: !review.quick_replies })
            }
            label="Quick Replies"
          />
        </div>
        <div className="w-full">
          {error && (
            <span className="text-xs font-semibold text-red-500">{error}</span>
          )}
          <FormTextarea
            placeholder="Leave a comment for the seller..."
            onChange={(e) => {
              setReview({ ...review, comment: e.target.value });
            }}
          />
        </div>
        <div className="flex w-full grow flex-col gap-[1.6rem] px-4 ">
          <span className="font-semibold">Images</span>
          <button
            type="button"
            className={cx([
              'h-[15rem] w-full cursor-pointer overflow-clip rounded-[1.6rem] outline-dashed outline-2 outline-brand-secondary-light hover:bg-brand-darker-white  focus:outline-brand-secondary',
              !!error ? 'outline-brand-red' : 'outline-brand-secondary-light',
            ])}
          >
            <label
              htmlFor="addPhotoInput"
              className="flex h-full w-full cursor-pointer flex-col  items-center justify-center gap-[1.3rem]"
            >
              {uploadingImage ? (
                <LoadingSpinner />
              ) : (
                <>
                  <AddPhotoIcon />
                  <p className="hidden text-[1.3rem] font-semibold text-brand-gray sm:block">
                    Click or drop your photos here
                  </p>
                </>
              )}
            </label>
            <input
              id="addPhotoInput"
              type="file"
              accept="image/*,video/*"
              multiple
              name="photo"
              className="hidden"
              onChange={handleFileInputChange}
            />
          </button>
          <ImageList images={review.images} deleteImage={deleteImage} />
        </div>
        <div className="flex w-full flex-col gap-[0.8rem] py-[2rem] font-semibold text-brand-secondary">
          <Button
            loading={loadingSubmit}
            width="fluid"
            type="secondary"
            text="Submit Feedback"
            onClick={() => {
              if (
                review.rating < 5 &&
                (!review.comment || review.comment.trim().length < 30)
              ) {
                setError(
                  'Please leave a comment for ratings less than 5 stars. Min 30 characters.'
                );
              } else {
                submitReviewMutation.mutate(review);
              }
            }}
          />
          <div className="hidden w-full sm:block">
            <Button
              width="fluid"
              type="text"
              text="Cancel"
              onClick={() => dismiss?.()}
            />
          </div>
        </div>
      </div>
    </BaseModal>
  );
};

export default LeaveFeedbackModal;
